
// StepTriage.jsx
// Animated analysis → dynamic triage + full score & risk display after loader
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./StepTriage.module.css";
import {
  getScreeningTriage,
  getHighestSeverityResult,
} from "./SelectSymptomsConstants";

// ─────────────────────────────────────────────────────────
// Analysis steps  
// ─────────────────────────────────────────────────────────
const ANALYSIS_STEPS = [
  { id: 1, label: "Analysing selected symptoms",          duration: 900  },
  { id: 2, label: "Cross-referencing medical data",       duration: 1200 },
  { id: 3, label: "Evaluating screening scores",          duration: 1100 },
  { id: 4, label: "Checking clinical risk signals",       duration: 900  },
  { id: 5, label: "Generating personalised triage",       duration: 1000 },
];

// ─────────────────────────────────────────────────────────
// Triage level definitions
// ─────────────────────────────────────────────────────────
const TRIAGE_LEVELS = {
  emergency: { level: "Emergency",   code: "emergency", color: "#DC2626", bg: "#FEF2F2", border: "#FCA5A5", icon: "🚨", headline: "Seek immediate medical attention", message: "Your symptoms and screening results suggest a potentially serious condition requiring urgent evaluation. Please go to the emergency department or call emergency services immediately.", timeframe: "Within 1 hour",     badge: "URGENT"   },
  urgent:    { level: "Urgent Care", code: "urgent",    color: "#EA580C", bg: "#FFF7ED", border: "#FDBA74", icon: "⚠️", headline: "See a doctor today",                message: "Your screening results indicate elevated clinical risk requiring prompt medical evaluation within the next 24 hours. Please contact your GP or visit an urgent care clinic.",               timeframe: "Within 24 hours",   badge: "URGENT"   },
  semi:      { level: "Semi-Urgent", code: "semi",      color: "#0284C7", bg: "#EFF6FF", border: "#BFDBFE", icon: "🔵", headline: "Schedule a doctor visit soon",      message: "Your condition should be evaluated within the next few days. Book an appointment with your primary care physician or a relevant specialist.",                                            timeframe: "Within 2–3 days",   badge: "MODERATE" },
  routine:   { level: "Non-Urgent",  code: "routine",   color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0", icon: "✅", headline: "Routine consultation recommended",   message: "Your results do not indicate immediate concern. A general check-up with your doctor is advisable to review your symptoms and confirm your wellness.",                                   timeframe: "Within 1–2 weeks",  badge: "ROUTINE"  },
};

// ─────────────────────────────────────────────────────────
// computeTriage — priority:
//   1. Critical flags (PHQ-9 Q9)   → emergency
//   2. Screening scoreData         → maps via getScreeningTriage
//   3. Symptom keyword signals     → fallback
// ─────────────────────────────────────────────────────────
function computeTriage(wizardData) {
  const symptoms   = wizardData.symptoms   || [];
  const statements = wizardData.statements || [];
  const screening  = wizardData.screening  || {};
  const scoreData  = screening.scoreData;

  // ── 1. Screening-driven ──────────────────────────────────
  if (scoreData) {
    const { triageMeta, highest } = scoreData;

    if (highest?.criticalFlags?.length > 0) {
      return {
        ...TRIAGE_LEVELS.emergency,
        clinicalNote: `Critical flag: ${highest.criticalFlags[0].alert}`,
        scoreData,
      };
    }

    if (triageMeta?.code === "urgent") {
      const level = ["Severe", "Moderately Severe", "High Risk"].includes(highest?.severity)
        ? TRIAGE_LEVELS.urgent
        : TRIAGE_LEVELS.semi;
      return { ...level, clinicalNote: triageMeta.label, scoreData };
    }
    if (triageMeta?.code === "semi")    return { ...TRIAGE_LEVELS.semi,    clinicalNote: triageMeta.label, scoreData };
    if (triageMeta?.code === "routine") return { ...TRIAGE_LEVELS.routine, clinicalNote: triageMeta.label, scoreData };
  }

  // ── 2. Symptom keyword fallback ──────────────────────────
  const EMERGENCY_SYMPTOMS = ["Chest Pain", "Shortness of Breath", "Palpitations"];
  const URGENT_SYMPTOMS    = ["Fever", "Dizziness", "Headache", "Migraine"];
  const WARNING_STATEMENTS = [
    "My symptoms are severe and disabling",
    "My symptoms are getting worse",
    "I am unable to do my normal activities",
  ];

  const hasEmergency = symptoms.some((s) =>
    EMERGENCY_SYMPTOMS.some((e) => s.label?.toLowerCase().includes(e.toLowerCase()))
  );
  const hasUrgent    = symptoms.some((s) =>
    URGENT_SYMPTOMS.some((u) => s.label?.toLowerCase().includes(u.toLowerCase()))
  );
  const hasWarning = Array.isArray(statements)
    ? statements.some((s) => WARNING_STATEMENTS.includes(s))
    : false;

  if (hasEmergency)              return { ...TRIAGE_LEVELS.emergency, scoreData: null };
  if (hasUrgent || hasWarning)   return { ...TRIAGE_LEVELS.urgent,    scoreData: null };
  if (symptoms.length >= 3)      return { ...TRIAGE_LEVELS.semi,      scoreData: null };
  return                                { ...TRIAGE_LEVELS.routine,   scoreData: null };
}

// ─────────────────────────────────────────────────────────
// computeBodySystems
// ─────────────────────────────────────────────────────────
function computeBodySystems(symptoms) {
  const map = {
    "Cardiovascular":       ["Chest Pain", "Palpitations", "Shortness of Breath"],
    "Neurological":         ["Headache", "Migraine", "Dizziness", "Numbness"],
    "Digestive":            ["Nausea", "Stomach Pain", "Bloating", "Vomiting", "Constipation"],
    "Respiratory":          ["Cough", "Wheezing", "Shortness of Breath"],
    "Reproductive":         ["Pelvic Pain", "Irregular Periods", "Heavy Bleeding"],
    "Musculoskeletal":      ["Back Pain", "Joint Pain", "Muscle Ache", "Stiffness"],
    "Mental & Emotional":   ["Anxiety", "Depression", "Stress", "Insomnia", "Mood Swings"],
  };
  return Object.entries(map)
    .map(([system, keys]) => {
      const matched = symptoms.filter((s) =>
        keys.some((k) => s.label?.toLowerCase().includes(k.toLowerCase()))
      );
      return matched.length > 0
        ? { system, count: matched.length, symptoms: matched.map((m) => m.label) }
        : null;
    })
    .filter(Boolean);
}

// ─────────────────────────────────────────────────────────
// AnalysisLoader
// ─────────────────────────────────────────────────────────
function AnalysisLoader({ onComplete }) {
  const [currentStep,    setCurrentStep]    = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  useEffect(() => {
    let idx = 0;
    const run = () => {
      if (idx >= ANALYSIS_STEPS.length) { setTimeout(onComplete, 400); return; }
      setCurrentStep(idx);
      setTimeout(() => {
        setCompletedSteps((p) => [...p, idx]);
        idx++;
        run();
      }, ANALYSIS_STEPS[idx].duration);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pct = Math.round((completedSteps.length / ANALYSIS_STEPS.length) * 100);

  return (
    <div className={styles.loaderWrap}>
      <div className={styles.loaderCard}>
        <div className={styles.loaderPulse}>
          <div className={styles.loaderRing}  />
          <div className={styles.loaderRing2} />
          <svg className={styles.loaderHeartIcon} viewBox="0 0 24 24" fill="none"
            stroke="var(--primary-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
          </svg>
        </div>
        <h2 className={styles.loaderTitle}>Analysing your responses</h2>
        <p className={styles.loaderSub}>Our clinical algorithm is processing your screening results…</p>
        <div className={styles.loaderTrack}>
          <div className={styles.loaderFill} style={{ width: `${pct}%` }} />
        </div>
        <span className={styles.loaderPct}>{pct}%</span>
        <div className={styles.loaderSteps}>
          {ANALYSIS_STEPS.map((step, i) => {
            const done    = completedSteps.includes(i);
            const current = i === currentStep && !done;
            return (
              <div key={step.id} className={`${styles.loaderStep} ${done ? styles.loaderStepDone : ""} ${current ? styles.loaderStepActive : ""}`}>
                <span className={styles.loaderStepDot}>
                  {done ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : current ? <span className={styles.loaderSpinner} /> : "·"}
                </span>
                <span className={styles.loaderStepLabel}>{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// ScoreResultCard — per-assessment in the result view
// ─────────────────────────────────────────────────────────
function ScoreResultCard({ result }) {
  if (!result) return null;
  const { title, shortTitle, totalScore, maxScore, severity, severityMeta, criticalFlags, needsClinicalAttention } = result;
  const color = severityMeta?.color || "#6B7280";
  const pct   = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

  return (
    <div className={styles.scoreCard}>
      <div className={styles.scoreCardHeader}>
        <div>
          <div className={styles.scoreCardTitle}>{title}</div>
          <div className={styles.scoreCardSub}>{shortTitle}</div>
        </div>
        <span className={styles.scoreCardBadge} style={{ background: color + "18", color, border: `1.5px solid ${color}40` }}>
          {severity}
        </span>
      </div>
      <div className={styles.scoreBarRow}>
        <span className={styles.scoreBarLabel}>Score</span>
        <span className={styles.scoreBarValue} style={{ color }}>{totalScore} / {maxScore} ({pct}%)</span>
      </div>
      <div className={styles.scoreBarTrack}>
        <div className={styles.scoreBarFill} style={{ width: `${pct}%`, background: color }} />
      </div>
      {severityMeta?.recommendation && (
        <p className={styles.scoreCardRec}>{severityMeta.recommendation}</p>
      )}
      {criticalFlags?.length > 0 && (
        <div className={styles.criticalBanner}>🚨 <strong>Critical response detected.</strong> Immediate clinical attention required.</div>
      )}
      {needsClinicalAttention && !criticalFlags?.length && (
        <div className={styles.attentionBanner}>⚠️ Clinical evaluation is recommended based on this score.</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// TriageResult — FULL result page after analysis
// ─────────────────────────────────────────────────────────
function TriageResult({ triage, bodySystems, symptomCount, screening, onNext }) {
  const scoreData  = triage.scoreData;
  const hasScoring = scoreData && Array.isArray(scoreData.results) && scoreData.results.length > 0;

  return (
    <div className={styles.resultWrap}>

      {/* ── Main triage card ── */}
      <div className={styles.triageCard} style={{ background: triage.bg, borderColor: triage.border }}>
        <div className={styles.triageTop}>
          <div className={styles.triageIconWrap}
            style={{ background: triage.color + "18", border: `2px solid ${triage.color}40` }}>
            <span className={styles.triageIcon}>{triage.icon}</span>
          </div>
          <div>
            <div className={styles.triageBadge} style={{ background: triage.color + "20", color: triage.color }}>
              {triage.badge}
            </div>
            <h2 className={styles.triageLevel} style={{ color: triage.color }}>{triage.level}</h2>
            <p className={styles.triageHeadline}>{triage.headline}</p>
          </div>
        </div>
        <p className={styles.triageMessage}>{triage.message}</p>
        {triage.clinicalNote && (
          <p className={styles.triageClinicalNote}>📋 Clinical note: {triage.clinicalNote}</p>
        )}
        <div className={styles.triageTimeframe}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          <strong>Recommended timeframe:</strong> {triage.timeframe}
        </div>
      </div>

      {/* ── Score & Risk Breakdown (from screening) ── */}
      {/* {hasScoring && (
        <div className={styles.scoringSection}>
          <h3 className={styles.sectionTitle}><span>📊</span> Score &amp; Risk Breakdown</h3>

          <div className={styles.scoreCards}>
            {scoreData.results.map((r) => r && <ScoreResultCard key={r.questionnaireId} result={r} />)}
          </div>

          <div className={styles.aggregateRow}>
            <div className={styles.aggregateStat}>
              <span className={styles.aggregateNum} style={{ color: triage.color }}>{scoreData.totalScore}</span>
              <span className={styles.aggregateLbl}>Total Score</span>
            </div>
            <div className={styles.aggregateDivider} />
            <div className={styles.aggregateStat}>
              <span className={styles.aggregateNum}>{scoreData.maxScore}</span>
              <span className={styles.aggregateLbl}>Max Possible</span>
            </div>
            <div className={styles.aggregateDivider} />
            <div className={styles.aggregateStat}>
              <span className={styles.aggregateNum} style={{ color: triage.color }}>
                {Math.round((scoreData.totalScore / scoreData.maxScore) * 100)}%
              </span>
              <span className={styles.aggregateLbl}>Severity %</span>
            </div>
            <div className={styles.aggregateDivider} />
            <div className={styles.aggregateStat}>
              <span className={styles.aggregateNum} style={{ fontSize: 13, fontWeight: 800, color: triage.color }}>
                {scoreData.highest?.severity || "—"}
              </span>
              <span className={styles.aggregateLbl}>Highest Severity</span>
            </div>
          </div>
        </div>
      )} */}

      {/* ── Body systems affected ── */}
      {/* {bodySystems.length > 0 && (
        <div className={styles.systemsCard}>
          <h3 className={styles.systemsTitle}>Body systems involved</h3>
          <div className={styles.systemsGrid}>
            {bodySystems.map((sys) => (
              <div key={sys.system} className={styles.systemItem}>
                <span className={styles.systemName}>{sys.system}</span>
                <span className={styles.systemCount}>{sys.count} symptom{sys.count !== 1 ? "s" : ""}</span>
                <div className={styles.systemTags}>
                  {sys.symptoms.map((s) => (
                    <span key={s} className={styles.systemTag}>{s}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )} */}

      {/* ── Summary stats ── */}
      {/* <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statNum}>{symptomCount}</span>
          <span className={styles.statLbl}>Symptoms logged</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statNum}>{bodySystems.length}</span>
          <span className={styles.statLbl}>Systems involved</span>
        </div>
        {hasScoring && (
          <div className={styles.statCard}>
            <span className={styles.statNum}>{scoreData.results.length}</span>
            <span className={styles.statLbl}>Screenings done</span>
          </div>
        )}
        <div className={styles.statCard}>
          <span className={styles.statNum} style={{ color: triage.color, fontSize: 13, fontWeight: 800 }}>
            {triage.level}
          </span>
          <span className={styles.statLbl}>Triage level</span>
        </div>
      </div> */}
      {/* ── QUICK ACTIONS ── */}
      <div className={styles.resultsQuickActionsWrap}>
        <div className={styles.resultsQuickActionsHeader}>
          <h3 className={styles.resultsQuickActionsTitle}>
            Recommended Next Actions 
          </h3>

          <p className={styles.resultsQuickActionsSubtitle}>
            Continue your care journey with specialist guidance and personalized support.
          </p>
        </div>

        <div className={styles.resultsActionsGrid}>
          {[
            {
              title: "Book Appointment",
              desc: "Consult a specialist doctor",
              bg: "#F8F9F9",
              icon: "📅",
              link: "/book-appointment",
            },
            {
              title: "Chat with Expert",
              desc: "Get guidance and support",
              bg: "#E3F2FD",
              icon: "💬",
              link: "/chat",
            },
            {
              title: "Enroll Program",
              desc: "Personalized wellness plans",
              bg: "#FEF9E7",
              icon: "✨",
              link: "/programs",
            },
          ].map((action, idx) => (
            <Link
              key={idx}
              to={action.link}
              className={styles.resultsActionCard}
              style={{ background: action.bg }}
            >
              <div className={styles.resultsActionIcon}>
                {action.icon}
              </div>

              <div className={styles.resultsActionBody}>
                <div className={styles.resultsActionTitle}>
                  {action.title}
                </div>

                <div className={styles.resultsActionDesc}>
                  {action.desc}
                </div>
              </div>

              <div className={styles.resultsActionArrow}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* ── CTAs ── */}
      {/* <button className={styles.resultsBtn} onClick={() => onNext(triage)}>
        View Full Results Report
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
      </button> */}

      {/* <Link to="/book-appointment" className={styles.continueBtn}>
        View Recommended Doctors
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
      </Link> */}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────
export default function StepTriage({ wizardData, onNext, onBack }) {
  const [phase, setPhase] = useState("loading");

  const triage      = computeTriage(wizardData);
  const bodySystems = computeBodySystems(wizardData.symptoms || []);

  return (
    <div className={styles.root}>
      {phase === "loading" ? (
        <AnalysisLoader onComplete={() => setPhase("result")} />
      ) : (
        <TriageResult
          triage={triage}
          bodySystems={bodySystems}
          symptomCount={wizardData.symptoms?.length || 0}
          screening={wizardData.screening}
          onNext={(data) => onNext({ ...data, triageLevel: triage.level, triageCode: triage.code })}
        />
      )}
    </div>
  );
}


// import { useState, useEffect } from "react";
// import StepLayout from "./StepLayout";
// import styles from "./StepTriage.module.css";
// import { Link } from "react-router-dom";

// const ANALYSIS_STEPS = [
//   { id: 1, label: "Analysing symptoms", duration: 1000 },
//   { id: 2, label: "Cross-referencing medical data", duration: 1400 },
//   { id: 3, label: "Evaluating severity signals", duration: 1200 },
//   { id: 4, label: "Checking contraindications", duration: 1000 },
//   { id: 5, label: "Generating triage assessment", duration: 1100 },
// ];

// function computeTriage(wizardData) {
//   const symptoms = wizardData.symptoms || [];
//   const statements = wizardData.statements || [];
//   const questionnaire = wizardData.questionnaire || {};

//   // Emergency keywords
//   const EMERGENCY_SYMPTOMS = ["Chest pain", "Shortness of breath", "Palpitations"];
//   const URGENT_SYMPTOMS = ["Fever", "Dizziness", "Headache", "Migraine", "Severe"];
//   const WARNING_STATEMENTS = [
//     "My symptoms are severe and disabling",
//     "My symptoms are getting worse",
//     "I am unable to do my normal activities",
//   ];

//   const hasEmergency = symptoms.some(s => EMERGENCY_SYMPTOMS.some(e => s.label.includes(e)));
//   const hasUrgent = symptoms.some(s => URGENT_SYMPTOMS.some(u => s.label.includes(u)));
//   const hasWarning = statements.some(s => WARNING_STATEMENTS.includes(s));
//   const severity = questionnaire.severity || 0;

//   if (hasEmergency || severity >= 8) {
//     return {
//       level: "Emergency",
//       code: "emergency",
//       color: "#dc2626",
//       bg: "#fef2f2",
//       border: "#fca5a5",
//       icon: "🚨",
//       headline: "Seek immediate medical attention",
//       message: "Your symptoms suggest a potentially serious condition requiring urgent evaluation. Please go to the emergency department or call emergency services immediately.",
//       timeframe: "Within 1 hour",
//       badge: "URGENT",
//     };
//   }

//   if (hasUrgent || hasWarning || severity >= 6) {
//     return {
//       level: "Urgent Care",
//       code: "urgent",
//       color: "var(--ava-color-warning-base)",
//       bg: "var(--ava-color-warning-100)",
//       border: "#fde68a",
//       icon: "⚠️",
//       headline: "See a doctor today",
//       message: "Your symptoms require prompt medical evaluation within the next 24 hours. Contact your GP or visit an urgent care clinic.",
//       timeframe: "Within 24 hours",
//       badge: "URGENT",
//     };
//   }

//   if (symptoms.length >= 3 || severity >= 4) {
//     return {
//       level: "Semi-Urgent",
//       code: "semi",
//       color: "#0284c7",
//       bg: "#eff6ff",
//       border: "#bfdbfe",
//       icon: "🔵",
//       headline: "Schedule a doctor visit soon",
//       message: "Your condition should be evaluated within the next few days. Book an appointment with your primary care physician or a specialist.",
//       timeframe: "Within 2–3 days",
//       badge: "MODERATE",
//     };
//   }

//   return {
//     level: "Non-Urgent",
//     code: "routine",
//     color: "#16a34a",
//     bg: "#f0fdf4",
//     border: "#bbf7d0",
//     icon: "✅",
//     headline: "Routine consultation recommended",
//     message: "Your symptoms do not appear to require immediate attention. However, a general check-up with your doctor is advisable to rule out any underlying conditions.",
//     timeframe: "Within 1–2 weeks",
//     badge: "ROUTINE",
//   };
// }

// function computeBodySystems(symptoms) {
//   const map = {
//     cardiovascular: ["Chest pain", "Palpitations", "Shortness of breath"],
//     neurological: ["Headache", "Migraine", "Dizziness", "Numbness"],
//     digestive: ["Nausea", "Stomach pain", "Bloating", "Vomiting", "Constipation"],
//     respiratory: ["Cough", "Wheezing", "Shortness of breath"],
//     reproductive: ["Pelvic pain", "Irregular periods", "Heavy bleeding"],
//     musculoskeletal: ["Back pain", "Joint pain", "Muscle ache", "Stiffness"],
//     psychological: ["Anxiety", "Depression", "Stress", "Insomnia"],
//   };

//   const results = [];
//   Object.entries(map).forEach(([sys, keys]) => {
//     const matched = symptoms.filter(s => keys.some(k => s.label.toLowerCase().includes(k.toLowerCase())));
//     if (matched.length > 0) {
//       results.push({ system: sys, count: matched.length, symptoms: matched.map(m => m.label) });
//     }
//   });
//   return results;
// }

// /* ── Loading screen ─────────────────────────────────────────────────── */
// function AnalysisLoader({ onComplete }) {
//   const [currentStep, setCurrentStep] = useState(0);
//   const [completedSteps, setCompletedSteps] = useState([]);

//   useEffect(() => {
//     let idx = 0;
//     const run = () => {
//       if (idx >= ANALYSIS_STEPS.length) {
//         setTimeout(onComplete, 500);
//         return;
//       }
//       setCurrentStep(idx);
//       const dur = ANALYSIS_STEPS[idx].duration;
//       setTimeout(() => {
//         setCompletedSteps(prev => [...prev, idx]);
//         idx++;
//         run();
//       }, dur);
//     };
//     run();
//   }, []);

//   const total = ANALYSIS_STEPS.length;
//   const pct = Math.round((completedSteps.length / total) * 100);

//   return (
//     <div className={styles.loaderWrap}>
//       <div className={styles.loaderCard}>
//         {/* Pulse icon */}
//         <div className={styles.loaderPulse}>
//           <div className={styles.loaderRing} />
//           <div className={styles.loaderRing2} />
//           <svg className={styles.loaderHeartIcon} viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//             <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
//           </svg>
//         </div>

//         <h2 className={styles.loaderTitle}>Analysing your symptoms</h2>
//         <p className={styles.loaderSub}>Our clinical algorithm is processing your responses…</p>

//         {/* Progress bar */}
//         <div className={styles.loaderTrack}>
//           <div className={styles.loaderFill} style={{ width: `${pct}%` }} />
//         </div>
//         <span className={styles.loaderPct}>{pct}%</span>

//         {/* Steps */}
//         <div className={styles.loaderSteps}>
//           {ANALYSIS_STEPS.map((step, i) => {
//             const done = completedSteps.includes(i);
//             const current = i === currentStep && !done;
//             return (
//               <div key={step.id} className={`${styles.loaderStep} ${done ? styles.loaderStepDone : ""} ${current ? styles.loaderStepActive : ""}`}>
//                 <span className={styles.loaderStepDot}>
//                   {done ? (
//                     <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
//                       <polyline points="20 6 9 17 4 12" />
//                     </svg>
//                   ) : current ? (
//                     <span className={styles.loaderSpinner} />
//                   ) : "·"}
//                 </span>
//                 <span className={styles.loaderStepLabel}>{step.label}</span>
//               </div>
//             );
//           })}
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ── Result screen ──────────────────────────────────────────────────── */
// function TriageResult({ triage, bodySystems, symptomCount, onNext }) {
//   return (
//     <div className={styles.resultWrap}>
//       {/* Triage card */}
//       <div className={styles.triageCard} style={{ background: triage.bg, borderColor: triage.border }}>
//         <div className={styles.triageTop}>
//           <div className={styles.triageIconWrap} style={{ background: triage.color + "18", border: `2px solid ${triage.color}40` }}>
//             <span className={styles.triageIcon}>{triage.icon}</span>
//           </div>
//           <div>
//             <div className={styles.triageBadge} style={{ background: triage.color + "20", color: triage.color }}>
//               {triage.badge}
//             </div>
//             <h2 className={styles.triageLevel} style={{ color: triage.color }}>{triage.level}</h2>
//             <p className={styles.triageHeadline}>{triage.headline}</p>
//           </div>
//         </div>
//         <p className={styles.triageMessage}>{triage.message}</p>
//         <div className={styles.triageTimeframe}>
//           <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//             <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
//           </svg>
//           <strong>Recommended timeframe:</strong> {triage.timeframe}
//         </div>
//       </div>

//       {/* Body systems affected */}
//       {bodySystems.length > 0 && (
//         <div className={styles.systemsCard}>
//           <h3 className={styles.systemsTitle}>Body systems involved</h3>
//           <div className={styles.systemsGrid}>
//             {bodySystems.map(sys => (
//               <div key={sys.system} className={styles.systemItem}>
//                 <span className={styles.systemName}>
//                   {sys.system.charAt(0).toUpperCase() + sys.system.slice(1)}
//                 </span>
//                 <span className={styles.systemCount}>{sys.count} symptom{sys.count !== 1 ? "s" : ""}</span>
//                 <div className={styles.systemTags}>
//                   {sys.symptoms.map(s => (
//                     <span key={s} className={styles.systemTag}>{s}</span>
//                   ))}
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       )}

//       {/* Summary stats */}
//       <div className={styles.statsRow}>
//         <div className={styles.statCard}>
//           <span className={styles.statNum}>{symptomCount}</span>
//           <span className={styles.statLbl}>Symptoms logged</span>
//         </div>
//         <div className={styles.statCard}>
//           <span className={styles.statNum}>{bodySystems.length}</span>
//           <span className={styles.statLbl}>Systems involved</span>
//         </div>
//         <div className={styles.statCard}>
//           <span className={styles.statNum} style={{ color: triage.color }}>{triage.level}</span>
//           <span className={styles.statLbl}>Triage level</span>
//         </div>
//       </div>

//       <button className={styles.continueBtn} onClick={onNext}>
//         View Recommended Results
//         <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
//           <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
//         </svg>
//       </button>

//       <Link to="/book-appointment" className={styles.continueBtn}>
//         View Recommended Doctors
//         <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
//           <line x1="5" y1="12" x2="19" y2="12" />
//           <polyline points="12 5 19 12 12 19" />
//         </svg>
//       </Link>
//     </div>
//   );
// }

// /* ── Main component ─────────────────────────────────────────────────── */
// export default function StepTriage({ wizardData, onNext }) {
//   const [phase, setPhase] = useState("loading"); // "loading" | "result"

//   const triage = computeTriage(wizardData);
//   const bodySystems = computeBodySystems(wizardData.symptoms || []);

//   return (
//     <div className={styles.root}>
//       {phase === "loading" ? (
//         <AnalysisLoader onComplete={() => setPhase("result")} />
//       ) : (
//         <TriageResult
//           triage={triage}
//           bodySystems={bodySystems}
//           symptomCount={wizardData.symptoms?.length || 0}
//           onNext={() => onNext(triage)}
//         />
//       )}
//     </div>
//   );
// }
